import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { fetchMediaKitShareLink, shareMediaKit } from '../services/mediaKitService';
import '../styles/share-profile-modal.css';

interface ShareMediaKitModalProps {
    open: boolean;
    onClose: () => void;
    athleteUserId: number;
}

export default function ShareMediaKitModal({ open, onClose, athleteUserId }: ShareMediaKitModalProps) {
    const [emails, setEmails] = useState<string[]>([]);
    const [emailInput, setEmailInput] = useState('');
    const [message, setMessage] = useState('');
    const [shareableLink, setShareableLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const emailInputRef = useRef<HTMLInputElement>(null);

    const fetchShareableLink = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const link = await fetchMediaKitShareLink(athleteUserId);
            setShareableLink(link);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to load shareable link');
        } finally {
            setLoading(false);
        }
    }, [athleteUserId]);

    useEffect(() => {
        if (open && athleteUserId) {
            fetchShareableLink();
        }
    }, [open, athleteUserId, fetchShareableLink]);

    const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addEmailFromInput = () => {
        const trimmedEmail = emailInput.trim();
        if (!trimmedEmail) return;
        if (!validateEmail(trimmedEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }
        if (emails.includes(trimmedEmail)) {
            setEmailError('Email already added');
            return;
        }
        setEmails((prev) => [...prev, trimmedEmail]);
        setEmailInput('');
        setEmailError(null);
    };

    const handleShare = async () => {
        if (emailInput.trim()) {
            addEmailFromInput();
            return;
        }
        if (emails.length === 0) {
            setEmailError('Please add at least one email address');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            await shareMediaKit(athleteUserId, emails, message.trim() || undefined);
            setSuccess(true);
            setTimeout(handleClose, 2000);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to share media kit');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setEmails([]);
        setEmailInput('');
        setMessage('');
        setError(null);
        setSuccess(false);
        setEmailError(null);
        onClose();
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareableLink);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            // ignore
        }
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div className="share-modal-overlay" onClick={handleClose}>
            <div className="share-modal-card" onClick={(e) => e.stopPropagation()}>
                <button className="share-modal-close" onClick={handleClose} aria-label="Close modal">×</button>
                <div className="share-modal-content">
                    <h2 className="share-modal-title">Share Media Kit</h2>
                    {error && <div className="share-modal-error">{error}</div>}
                    {success && <div className="share-modal-success">Media kit shared successfully!</div>}
                    <div className="share-modal-form">
                        <div className="share-form-group">
                            <label className="share-form-label">EMAIL ADDRESSES</label>
                            <div className={`share-email-input-container${emailError ? ' error' : ''}`}>
                                {emails.map((email) => (
                                    <div key={email} className="share-email-tag">
                                        <span className="email-tag-text">{email}</span>
                                        <button type="button" className="email-tag-remove" onClick={() => setEmails((p) => p.filter((e) => e !== email))}>×</button>
                                    </div>
                                ))}
                                <input
                                    ref={emailInputRef}
                                    type="text"
                                    className="email-input-inline"
                                    value={emailInput}
                                    onChange={(e) => setEmailInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addEmailFromInput(); } }}
                                    placeholder={emails.length === 0 ? 'coach@example.com' : ''}
                                    disabled={loading || success}
                                    data-testid="share-media-kit-email-input"
                                />
                            </div>
                            {emailError && <span className="share-form-error">{emailError}</span>}
                            <span className="share-form-hint">
                                Press Enter or comma to add multiple email addresses
                            </span>
                        </div>
                        <div className="share-form-group">
                            <label className="share-form-label">MESSAGE (OPTIONAL)</label>
                            <textarea
                                className="share-form-textarea"
                                value={message}
                                onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                                placeholder="Hi Coach — here is my media kit. Looking forward to connecting!"
                                rows={4}
                                disabled={loading || success}
                                data-testid="share-media-kit-message-input"
                            />
                        </div>
                        <div className="share-form-group">
                            <div className="share-link-label-row">
                                <label className="share-form-label">COPY LINK TO CLIPBOARD</label>
                                <button type="button" className="share-copy-link-btn" onClick={handleCopyLink} disabled={!shareableLink || loading} data-testid="share-media-kit-copy-link">
                                    {linkCopied ? 'Copied!' : 'Copy Link'}
                                </button>
                            </div>
                            <input type="text" className="share-form-input share-link-input" value={shareableLink} readOnly data-testid="share-media-kit-link-input" />
                        </div>
                    </div>
                    <div className="share-modal-actions">
                        <button className="share-btn share-btn-secondary" onClick={handleClose} disabled={loading}>Close</button>
                        <button className="share-btn share-btn-primary" onClick={handleShare} disabled={loading || success} data-testid="share-media-kit-submit">Share</button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
